/**
 * Identifies the frequency mode of a communication channel (whether it is used to transmit, receive, or both). Note
 * that this mode pertains to the communication channel itself - what the pilot can do will be the reverse (e.g., if
 * the channel is only used to transmit, that means the pilot can only receive, and vice-versa).
 */
export enum FrequencyMode {
  /**
   * Communication channel may only be used to receive transmissions (pilot may transmit but not listen)
   */
  Receive,

  /**
   * Communication channel is only used for transmitting (pilot may only listen, not transmit)
   */
  Transmit,

  /**
   * Communication channel is duplex (may be used to both transmit and listen)
   */
  Duplex,

  /**
   * Communication channel is unknown
   */
  Unknown
}


/**
 * Flight communication channel (e.g., CTAF, ASOS/AWOS, ATIS, flight service, etc.)
 */
export class FlightComm {
  /**
   * Unique identifier for this record in the database
   */
  flightCommId: number;

  /**
   * Airport record ID for which this record applies
   */
  airportId: number;

  /**
   * Service type provided via this communication channel
   */
  service: string;

  /**
   * Radio call used by pilot to make initial contact over this communication channel
   */
  radioCall: string;

  /**
   * Frequency mode of the communication channel (receive-only, transmit-only, or duplex).
   */
  freqMode: FrequencyMode;

  /**
   * Channel frequency
   */
  frequency: string;

  /**
   * Sectorization of this communication channel based on master airport or airway boundaries, or limitations based on
   * runway usage (e.g., "SW 6000 FT & BLO", or a radial range such as "330-150").
   */
  sector: string;

  /**
   * Phone number for this communication channel (if available).
   */
  phoneNumber: string;

  /**
   * Unique Identifier for the frequency record in the database
   */
  frequencyId: number;

  /**
   * Short Name of flight comm
   */
  shortName: string;

  /**
   * type associated with the phone number
   */
  phoneType: string;

  /**
   * Gets the formatted frequency for display, which includes the T or R suffix for non-duplex channels.
   * @returns {string}
   */
  get formattedFreq(): string {
    if (!this.frequency) {
      return '';
    }
    switch (this.freqMode) {
      case FrequencyMode.Receive:
        return `${this.frequency}R`;
      case FrequencyMode.Transmit:
        return `${this.frequency}T`;
      default:
        return this.frequency;
    }
  }

  /**
   * Parses flight communication channel
   * @param data {any} flight communication channel as returned by API
   * @return {FlightComm} Parsed flight communication channel
   */
  static parse(data: any): FlightComm {
    return Object.assign(new FlightComm(), {
      flightCommId: data.flightCommId,
      airportId: data.airportId,
      service: data.service,
      radioCall: data.radioCall,
      freqMode: this.parseFrequencyMode(data.freqMode),
      frequency: data.frequency,
      sector: data.sector,
      phoneNumber: data.phoneNumber,
      frequencyId: data.frequencyId,
      shortName: data.shortName,
      phoneType: data.phoneType
    });
  }

  /**
   * Parses frequency mode
   * @param value {any} Frequency mode as returned by API
   * @return {FrequencyMode} Parsed frequency mode
   */
  static parseFrequencyMode(value: any): FrequencyMode {
    switch (value) {
      case 'Receive':
        return FrequencyMode.Receive;
      case 'Transmit':
        return FrequencyMode.Transmit;
      case 'Duplex':
        return FrequencyMode.Duplex;
      case 'Unknown':
        return FrequencyMode.Unknown;
      default:
        return null;
    }
  }

  /**
   * Formats frequency mode
   * @param {FrequencyMode} Frequency mode object
   * @return {string} Formatted frequency mode
   */
  static formatFrequencyMode(value: FrequencyMode): string {
    switch (value) {
      case FrequencyMode.Receive:
        return 'Receive';
      case FrequencyMode.Transmit:
        return 'Transmit';
      case FrequencyMode.Duplex:
        return 'Duplex';
      case FrequencyMode.Unknown:
        return 'Unknown';
    }
  }
}
